"use client";

import { useEffect, useState } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import MainLayout from "../../components/layout/MainLayout";
import {
  createSubscriptionAccessByOneToOtherUser,
  getSubscriptionDetailByUserId,
} from "../../../Services/subscriptionService";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

export function UserInviteForm({ onSubmit, onCancel }) {
  const currentLanguage = useSelector(
    (state) => state?.global?.current_language
  );
  const customerId = useSelector((state) => state?.auth?.loggedInUser?._id);
  const navigate = useNavigate();
  const [customerSubscriptionDetails, setCustomerSubscriptionDetails] =
    useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [formErrors, setFormErrors] = useState({});

  const [userDetails, setUserDetails] = useState({
    userName: "",
    userEmail: "",
    phoneNumber: "",
    company: "",
    password: "",
    accessLevel: "1",
    userId: Math.floor(Math.random() * (999999 - 100000) + 100000).toString(),
  });

  useEffect(() => {
    getUserSubscriptionDetail(customerId);
  }, [customerId]);

  async function getUserSubscriptionDetail(customerId) {
    if (!customerId) {
      setIsInitialLoading(false);
      return;
    }

    try {
      setIsInitialLoading(true);
      const response = await getSubscriptionDetailByUserId(customerId);
    } catch (error) {
      console.error("Error fetching subscription details:", error);
      toast.error("Failed to load subscription details");
    } finally {
      setIsInitialLoading(false);
    }
  }

  const validateForm = () => {
    const errors = {};

    if (!userDetails.userName.trim()) {
      errors.userName = "Name is required";
    }

    if (!userDetails.userEmail.trim()) {
      errors.userEmail = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(userDetails.userEmail)) {
      errors.userEmail = "Email is invalid";
    }

    if (!userDetails.phoneNumber.trim()) {
      errors.phoneNumber = "Phone number is required";
    }

    if (!userDetails.company.trim()) {
      errors.company = "Company name is required";
    }

    if (!userDetails.password) {
      errors.password = "Password is required";
    } else if (userDetails.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setUserDetails((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user types
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      const startTime = new Date();

      // Create a new object without _id and userId
      const subscriptionData = { ...customerSubscriptionDetails };
      delete subscriptionData["_id"];
      delete subscriptionData["userId"];

      const sendData = {
        ...subscriptionData,
        ...userDetails,
        startTime: startTime.toISOString(),
        paidSubscriptionByItself: false,
        subscribedBy: customerId,
        isInvitedUser: true,
      };

      const responseData = await createSubscriptionAccessByOneToOtherUser(
        sendData
      );

      if (responseData?.isEmailExist) {
        toast.error("Email already exists");
        setFormErrors((prev) => ({
          ...prev,
          userEmail: "Email already exists",
        }));
        return;
      }

      if (responseData?.success) {
        toast.success("User added successfully");
        navigate("/admin/users-access");
        if (onSubmit) onSubmit();

        // Reset form
        setUserDetails({
          userName: "",
          userEmail: "",
          phoneNumber: "",
          company: "",
          password: "",
          accessLevel: "1",
          userId: Math.floor(
            Math.random() * (999999 - 100000) + 100000
          ).toString(),
        });
      } else {
        toast.error(responseData?.message || "Failed to add user");
      }
    } catch (error) {
      console.error("Error adding user:", error);
      toast.error(error?.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitialLoading) {
    return (
      <MainLayout title="Invite User">
        <div className="flex items-center justify-center h-[60vh]">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <p className="text-gray-500">Loading subscription details...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <MainLayout title="Invite User">
      <div className=" mx-auto py-8 px-4 sm:px-6">
        <div className="bg-white overflow-hidden">
          <div className="flex justify-between border-b bg-gray-50 px-6 py-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Invite User</h2>
              <p className="text-sm text-gray-600">
                Add a new user to your subscription
              </p>
            </div>
            <div onClick={handleGoBack}>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2">
                Go Back
              </button>
            </div>
          </div>
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label
                  htmlFor="userId"
                  className="block text-sm font-medium text-gray-700"
                >
                  User ID
                </label>
                <input
                  id="userId"
                  type="text"
                  value={userDetails.userId}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md font-medium bg-gray-50 text-gray-500 cursor-not-allowed"
                  disabled
                />
                <p className="text-xs text-gray-500">Auto-generated user ID</p>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="userName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="userName"
                  type="text"
                  placeholder="Enter full name"
                  value={userDetails.userName}
                  onChange={(e) =>
                    handleInputChange("userName", e.target.value)
                  }
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    formErrors.userName ? "border-red-500" : "border-gray-300"
                  }`}
                  required
                />
                {formErrors.userName && (
                  <p className="text-sm text-red-500">{formErrors.userName}</p>
                )}
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="userEmail"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="userEmail"
                  type="email"
                  placeholder="email@example.com"
                  value={userDetails.userEmail}
                  onChange={(e) =>
                    handleInputChange("userEmail", e.target.value)
                  }
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    formErrors.userEmail ? "border-red-500" : "border-gray-300"
                  }`}
                  required
                />
                {formErrors.userEmail && (
                  <p className="text-sm text-red-500">{formErrors.userEmail}</p>
                )}
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="phoneNumber"
                  className="block text-sm font-medium text-gray-700"
                >
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  id="phoneNumber"
                  type="tel"
                  placeholder="000-000-0000"
                  value={userDetails.phoneNumber}
                  onChange={(e) =>
                    handleInputChange("phoneNumber", e.target.value)
                  }
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    formErrors.phoneNumber
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  required
                />
                {formErrors.phoneNumber && (
                  <p className="text-sm text-red-500">
                    {formErrors.phoneNumber}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="company"
                  className="block text-sm font-medium text-gray-700"
                >
                  Company <span className="text-red-500">*</span>
                </label>
                <input
                  id="company"
                  type="text"
                  placeholder="Enter company name"
                  value={userDetails.company}
                  onChange={(e) => handleInputChange("company", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    formErrors.company ? "border-red-500" : "border-gray-300"
                  }`}
                  required
                />
                {formErrors.company && (
                  <p className="text-sm text-red-500">{formErrors.company}</p>
                )}
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="accessLevel"
                  className="block text-sm font-medium text-gray-700"
                >
                  Access Level <span className="text-red-500">*</span>
                </label>
                <select
                  id="accessLevel"
                  value={userDetails.accessLevel}
                  onChange={(e) =>
                    handleInputChange("accessLevel", e.target.value)
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  required
                >
                  <option value="0">Admin</option>
                  <option value="1">User</option>
                  <option value="2">Guest</option>
                </select>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={userDetails.password}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors pr-10 ${
                      formErrors.password ? "border-red-500" : "border-gray-300"
                    }`}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                    <span className="sr-only">
                      {showPassword ? "Hide password" : "Show password"}
                    </span>
                  </button>
                </div>
                {formErrors.password && (
                  <p className="text-sm text-red-500">{formErrors.password}</p>
                )}
              </div>

              <div className="flex gap-4 pt-2  w-max">
                <button
                  type="submit"
                  className={`w-full py-2.5 px-4 rounded-md font-medium text-white ${
                    isLoading
                      ? "bg-blue-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  }`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin h-5 w-5 mr-3"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Adding User...
                    </span>
                  ) : (
                    "Add User"
                  )}
                </button>
                {onCancel && (
                  <button
                    type="button"
                    onClick={onCancel}
                    className={`w-full py-2.5 px-4 border border-gray-300 rounded-md font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      isLoading ? "cursor-not-allowed opacity-50" : ""
                    }`}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
